import { useEffect, useMemo, useState } from 'react';

export const ADMIN_TABLE_PAGE_SIZE = 10;

function buildPaginationResetKey<T>(items: T[]): string {
  if (items.length === 0) return '0';
  const first = items[0] as { id?: string; code?: string; idProfil?: string };
  const last = items[items.length - 1] as { id?: string; code?: string; idProfil?: string };
  const keyOf = (row: typeof first) => row?.id ?? row?.code ?? row?.idProfil ?? '';
  return `${items.length}:${keyOf(first)}:${keyOf(last)}`;
}

export function useAdminTablePagination<T>(items: T[], pageSize = ADMIN_TABLE_PAGE_SIZE) {
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const resetKey = useMemo(() => buildPaginationResetKey(items), [items]);

  useEffect(() => {
    setCurrentPage(1);
  }, [resetKey, pageSize]);

  const safeCurrentPage = Math.min(currentPage, totalPages);

  const paginatedItems = useMemo(() => {
    const start = (safeCurrentPage - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, pageSize, safeCurrentPage]);

  const rangeStart = items.length === 0 ? 0 : (safeCurrentPage - 1) * pageSize + 1;
  const rangeEnd = Math.min(safeCurrentPage * pageSize, items.length);
  const summary =
    items.length === 0 ? 'Aucun élément' : `Affichage ${rangeStart}–${rangeEnd} sur ${items.length}`;

  return {
    currentPage: safeCurrentPage,
    totalPages,
    onPageChange: setCurrentPage,
    paginatedItems,
    summary,
  };
}
